import React, { useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { FaFileCode, FaFilePdf } from 'react-icons/fa';
import UploadResume from '../JobComponents/UploadResume';
import SectionHeader from '../Pages/Components/SectionHeader';
import { useSession } from '../Utils/SessionContext';

// Import ProfileShards components
import HardSkills from './ProfileShards/HardSkills';
import SoftSkills from './ProfileShards/SoftSkills';
import Expertise from './ProfileShards/Expertise';
import WorkExperience from './ProfileShards/WorkExperience';
import Projects from './ProfileShards/Projects';
import HonorsAndAwards from './ProfileShards/HonorsAndAwards';
import PersonalInfo from './ProfileShards/PersonalInfo';
import Bio from './ProfileShards/Bio';
import Education from './ProfileShards/Education';
import Certifications from './ProfileShards/Certifications';
import Languages from './ProfileShards/Languages';

const CandidateProfile = ({ profileData, candidate }) => {
  const { user } = useSession();
  const [isUploadResumeOpen, setIsUploadResumeOpen] = useState(false);
  
  // Helper function to format date if available
  const formatDate = (dateString) => {
    if (!dateString || dateString.includes("[")) return "N/A";
    return dateString;
  };

  // Check if the profile data has the expected structure
  // We'll check for common properties that should be in a candidate profile
  const isCandidateProfile = profileData && (
    profileData.firstname !== undefined || 
    (profileData.candidateProfile && profileData.candidateProfile.firstname !== undefined)
  );

  // We have candidate profile data, so let's display it
  // Check if the data is directly in profileData or nested in candidateProfile
  const candidateProfile = profileData ? (profileData.candidateProfile || profileData) : {};
  
  // Determine if upload resume button should be shown
  // Show if: profile doesn't exist OR user is Superadmin
  const shouldShowUploadButton = !profileData || user?.role === 'Superadmin';

  return (
    <div>
      {/* Tab-related header */}
      <SectionHeader
        title="Candidate Profile"
        description="This tab displays comprehensive information about the candidate, including personal details, professional summary, skills, work experience, education, certifications, and languages."
        actions={[
          ...(shouldShowUploadButton ? [
            <button 
              key="regenerate"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors whitespace-nowrap flex items-center"
              onClick={() => setIsUploadResumeOpen(true)}
            >
              <FiRefreshCw className="mr-2" /> Upload Resume (Regenerate)
            </button>
          ] : []),
          ...(profileData ? [
            <button 
              key="getJson"
              className="px-4 py-2 bg-gray-400 text-white rounded cursor-not-allowed opacity-70 whitespace-nowrap flex items-center"
              disabled
            >
              <FaFileCode className="mr-2" /> JSON
            </button>,
            <button 
              key="getPdf"
              className="px-4 py-2 bg-gray-400 text-white rounded cursor-not-allowed opacity-70 whitespace-nowrap flex items-center"
              disabled
            >
              <FaFilePdf className="mr-2" /> PDF
            </button>
          ] : [])
        ]}
      />
      
      {!profileData ? (
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-gray-600">Candidate profile data will be displayed here.</p>
        </div>
      ) : !isCandidateProfile ? (
        <div className="bg-gray-100 p-4 rounded overflow-auto max-h-96">
          <p className="text-gray-600 mb-2">Raw profile data (no candidate profile structure found):</p>
          <pre className="text-xs text-gray-700">
            {JSON.stringify(profileData, null, 2)}
          </pre>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row gap-6">
        {/* CV Column - 33% width */}
        <div className="w-full md:w-1/3">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Resume</h3>
              {candidate.signedURL ? (
                <div className="overflow-auto max-h-screen">
                  <iframe 
                    src={candidate.signedURL} 
                    className="w-full h-[800px] border-0"
                    title="Candidate CV"
                  ></iframe>
                </div>
              ) : (
                <p className="text-gray-500 italic">No resume available for this candidate.</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Profile Column - 67% width */}
        <div className="w-full md:w-2/3 space-y-8">
          {/* Personal Information */}
          <PersonalInfo candidateProfile={candidateProfile} roleCategory={candidate?.talent?.roleCategory} formatDate={formatDate} />

          {/* Bio */}
          <Bio bio={candidateProfile.bio} />

          {/* Skills Section - Two rows with Hard Skills on top and Soft Skills below */}
          <div className="space-y-6">
            {/* Hard Skills */}
            <HardSkills hardSkills={candidateProfile.hardSkills} />

            {/* Soft Skills */}
            <SoftSkills softSkills={candidateProfile.softSkills} />
          </div>

          {/* Expertise */}
          <Expertise expertise={candidateProfile.expertise} />

          {/* Work Experience */}
          <WorkExperience workExperiences={candidateProfile.workExperiences} formatDate={formatDate} />

          {/* Projects */}
          <Projects projects={candidateProfile.projects} />

          {/* Honors and Awards */}
          <HonorsAndAwards honorsAndAwards={candidateProfile.honorsAndAwards} />

          {/* Education, Certifications, Languages */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Education education={candidateProfile.education} />
            <Certifications certifications={candidateProfile.certifications} />
            <Languages languages={candidateProfile.languages} />
          </div>
        </div>
      </div>
      )}
      {/* Upload Resume Popup */}
      <UploadResume
        isOpen={isUploadResumeOpen}
        onClose={() => setIsUploadResumeOpen(false)}
        onSubmit={() => {}} // Empty handler as per requirements
        candidateId={candidate?.candidate_id}
        talentId={candidate?.talent_id}
        jobId={candidate?.job_id}
        context="candidate"
      />
    </div>
  );
};

export default CandidateProfile;
